/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormState,
  FormStateBuilder,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  getRouteInitData,
  isRouteForCreationMode,
  UserContext,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  FormMode,
  markControlAsDirty,
  setControlValue,
} from '@gh/core-ui';
import { isElementFocused, isNotNil } from '@gh/core-util';
import { SmsTemplateDetailsBusinessService } from '../../resources/sms-template-details-business.service';
import { SmsPlaceholder, SmsTemplateDetails } from '../../resources/sms-templates.model';
import { smsTemplatePreviewDialogRoute } from './sms-template-preview-dialog/sms-template-preview-dialog.component';

const BODY_ROW_HEIGHT_PX = 24;
const BODY_MIN_ROWS = 5;
const BODY_MAX_ROWS = 30;
const BODY_VIEWPORT_HEIGHT_CAP_RATIO = 0.8;

@Component({
  standalone: false,
  selector: 'gh-sms-template-details',
  templateUrl: 'sms-template-details.component.html',
  styleUrls: ['sms-template-details.component.scss'],
})
export class SmsTemplateDetailsComponent extends AbstractFormComponent<SmsTemplateDetails> implements OnInit, AfterViewInit {
  form: FormState<SmsTemplateDetails>;
  isPlaceholderAutocompleteOpen: boolean;

  @ViewChild('body', { read: ElementRef, static: false }) body: ElementRef;
  @ViewChild('nameField') fieldToFocus!: ElementRef;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private dialogs: DialogService,
              private userContext: UserContext,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private formStateBuilder: FormStateBuilder,
              private business: SmsTemplateDetailsBusinessService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  get pageTitle(): string {
    if (this.form.createMode) {
      return 'lbl_create_sms_template';
    } else if (this.form.editMode) {
      return 'lbl_edit_sms_template';
    } else {
      return 'lbl_sms_template';
    }
  }

  get completeFlow(): ActionFlow<FormFlowState<SmsTemplateDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<SmsTemplateDetails>) =>
        this.business.save(state.value)),
      finish: ({ value }) => {
        if (this.form.createMode) {
          this.router.switchTo(['/setup/sms-templates', value.id]);
        } else {
          this.form.setValue(value);
          setTimeout(() => this.resizeBodyField(), 0);
        }
      },
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    const mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode
      : this.userContext.hasPermission(BuilderPermission.Setup.SmsTemplates.EDIT)
        ? FormMode.EditMode
        : FormMode.ViewMode;

    this.form = this.formStateBuilder.buildPlain({
      mode,
      value: details,
      optimisticLock: true,
      enabled: whenCreateOrEditMode(),
      children: {
        id: void 0,
        name: { required: true },
        description: void 0,
        body: { required: true },
      },
    });
  }

  ngAfterViewInit(): void {
    if (this.form.mode === 1) {
      setTimeout(() => {
        this.fieldToFocus.nativeElement.focus();
        this.cdr.markForCheck();
      });
    }
    setTimeout(() => this.resizeBodyField(), 0);
  }

  resizeBodyField(): void {
    const el = this.body?.nativeElement as HTMLTextAreaElement | undefined;
    if (!el) {
      return;
    }
    const value = this.form?.group?.get('body')?.value ?? '';
    const hasValue = typeof value === 'string' && value.trim().length > 0;
    el.style.height = 'auto';
    if (!hasValue) {
      el.style.height = '40px';
      el.classList.remove('is-at-max-height');
      return;
    }
    const minH = BODY_ROW_HEIGHT_PX * BODY_MIN_ROWS;
    const rowCap = BODY_ROW_HEIGHT_PX * BODY_MAX_ROWS;
    const maxH =
      typeof window !== 'undefined'
        ? Math.min(rowCap, Math.floor(window.innerHeight * BODY_VIEWPORT_HEIGHT_CAP_RATIO))
        : rowCap;
    const h = Math.min(maxH, Math.max(minH, el.scrollHeight));
    el.style.height = `${h}px`;
    el.classList.toggle('is-at-max-height', h >= maxH);
  }

  private scrollTextareaCaretIntoView(textarea: HTMLTextAreaElement, caretIndex: number): void {
    const text = textarea.value;
    const idx = Math.max(0, Math.min(caretIndex, text.length));
    const cs = getComputedStyle(textarea);
    const mirror = document.createElement('div');
    mirror.style.cssText =
      'position:absolute;left:-99999px;top:0;visibility:hidden;overflow:hidden;' +
      'white-space:pre-wrap;word-wrap:break-word;overflow-wrap:break-word;';
    mirror.style.width = `${textarea.clientWidth}px`;
    mirror.style.font = cs.font;
    mirror.style.padding = cs.padding;
    mirror.style.border = cs.border;
    mirror.style.boxSizing = cs.boxSizing;

    mirror.appendChild(document.createTextNode(text.slice(0, idx)));
    const marker = document.createElement('span');
    marker.textContent = '\u200b';
    mirror.appendChild(marker);

    document.body.appendChild(mirror);
    const caretTop = marker.offsetTop;
    const lineHeight = parseFloat(cs.lineHeight);
    const lh = Number.isFinite(lineHeight) ? lineHeight : BODY_ROW_HEIGHT_PX;
    const caretBottom = caretTop + Math.max(marker.offsetHeight, lh);
    document.body.removeChild(mirror);

    const st = textarea.scrollTop;
    const viewH = textarea.clientHeight;
    if (caretTop < st) {
      textarea.scrollTop = caretTop;
    } else if (caretBottom > st + viewH) {
      textarea.scrollTop = caretBottom - viewH;
    }
  }

  onShowPreview(): void {
    this.dialogs.openWithInput(smsTemplatePreviewDialogRoute, { template: this.form.submitValue });
  }

  onSelectPlaceholder(placeholder: SmsPlaceholder): void {
    const input = this.body.nativeElement as HTMLTextAreaElement;
    // If focused (any caret) or a non-start selection was left on the element, insert there; else append.
    const positionToInsert =
      isElementFocused(input) || (isNotNil(input.selectionStart) && input.selectionStart !== 0)
        ? (input.selectionStart ?? 0)
        : input.value.length;

    const { value } = input;
    const token = `$${placeholder.code} `;
    const body = `${value.substring(0, positionToInsert)}${token}${value.substring(positionToInsert)}`;
    const caretAfterToken = positionToInsert + token.length;

    setControlValue(this.form.group, 'body', body);
    markControlAsDirty(this.form.group, 'body');
    this.isPlaceholderAutocompleteOpen = false;

   setTimeout(() => {
      input.focus();
      const len = body.length;
      const pos = Math.min(caretAfterToken, len);
      input.setSelectionRange(pos, pos);
      this.resizeBodyField();
      this.scrollTextareaCaretIntoView(input, pos);
    }, 0);
  }

  loadMorePlaceholders($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    load(this.business.findAllSmsPlaceholders(query));
  }
}
